/**
 * Microsoft Advertising click id (msclkid) capture + retrieval.
 *
 * When a user arrives from a Microsoft/Bing ad (with auto-tagging enabled), the
 * landing URL contains a `?msclkid=<token>` query parameter. We persist the most
 * recent value in a first-party cookie for 90 days (Microsoft's recommended
 * retention window) and send it to the API on order creation so conversions can
 * be attributed server-side via the UET Conversions API.
 *
 */

const COOKIE_NAME = 'msclkid';
const MAX_AGE_DAYS = 90;

// Per Microsoft's docs, msclkid is a guid that is always 32 characters long
const MSCLKID_32_RE = /^[a-z0-9]{32}$/i;
const MSCLKID_UUID_RE = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

function isValidMsclkid(value: string | null | undefined): value is string {
  return !!value && (MSCLKID_32_RE.test(value) || MSCLKID_UUID_RE.test(value));
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') {
    return;
  }
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Extract a `msclkid` value from a raw query-string-like segment.
 */
function msclkidFromQueryString(query: string): string | null {
  if (!query) {
    return null;
  }
  try {
    const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
    return params.get('msclkid');
  } catch {
    return null;
  }
}

/**
 * Read a valid `msclkid` directly from the current page URL, or null.
 *
 * Because the app uses hash-based routing (e.g. `/#/package-details/?msclkid=...`),
 * the param can arrive in one of two places:
 *   1. the real query string, before the hash: `?msclkid=x#/route`
 *   2. inside the hash fragment, after the route: `#/route?msclkid=x`
 * We check both so an ad-click landing URL is read regardless of structure.
 */
function readMsclkidFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // 1. Standard query string (before the '#').
  let fromUrl = msclkidFromQueryString(window.location.search);

  // 2. Fall back to a query string embedded in the hash fragment (after the '#').
  if (!isValidMsclkid(fromUrl)) {
    const hash = window.location.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex !== -1) {
      fromUrl = msclkidFromQueryString(hash.slice(qIndex));
    }
  }

  return isValidMsclkid(fromUrl) ? fromUrl : null;
}

/**
 * Read `msclkid` from the current URL and, if valid, persist it to a cookie.
 * Call once on app load. Overwrites any older stored value, per Microsoft guidance.
 */
export function captureMsclkidFromUrl(): void {
  try {
    // Prefer the URL value; fall back to Microsoft's own _uetmsclkid cookie
    const value = readMsclkidFromUrl() || readMsclkidFromUetCookie();
    if (value) {
      writeCookie(COOKIE_NAME, value, MAX_AGE_DAYS);
    }
  } catch {
    // Non-fatal: never let tracking break the app.
  }
}

/**
 * Recover the click id from Microsoft's own UET cookie as a last-resort fallback.
 */
function readMsclkidFromUetCookie(): string | null {
  const raw = readCookie('_uetmsclkid');
  if (!raw) {
    return null;
  }
  // Remove a leading "_uet" prefix if present, then validate the remainder.
  const stripped = raw.replace(/^_uet/i, '');
  return isValidMsclkid(stripped) ? stripped : null;
}

/**
 * Return a valid msclkid, or null.
 */
export function getMsclkid(): string | null {
  const stored = readCookie(COOKIE_NAME);
  if (isValidMsclkid(stored)) {
    return stored;
  }

  const fromUrl = readMsclkidFromUrl();
  if (fromUrl) {
    return fromUrl;
  }

  return readMsclkidFromUetCookie();
}

/**
 * Append the stored msclkid to a URL so it survives a cross-domain redirect
 * (e.g. from the WordPress-embedded widget to the React booking app).
 */
export function appendMsclkidToUrl(url: string): string {
  const msclkid = getMsclkid();
  if (!msclkid) {
    return url;
  }
  const separator = url.indexOf('?') === -1 ? '?' : '&';
  return `${url}${separator}msclkid=${encodeURIComponent(msclkid)}`;
}
